import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  applyBrandTheme,
  cacheBrand,
  cacheCompanySlug,
  readCachedBrand,
  readCachedCompanySlug,
} from './applyBrandTheme';
import { fetchBrandBySlug } from '../services/brandApi';
import { CompanyBrand, DEFAULT_COMPANY_BRAND, normalizeCompanyBrand } from './types';

interface BrandContextValue {
  brand: CompanyBrand;
  companySlug: string | null;
  setBrand: (brand: CompanyBrand) => void;
  loadBrandFromSlug: (slug: string) => Promise<CompanyBrand>;
}

const BrandContext = createContext<BrandContextValue>({
  brand: DEFAULT_COMPANY_BRAND,
  companySlug: null,
  setBrand: () => undefined,
  loadBrandFromSlug: async () => DEFAULT_COMPANY_BRAND,
});

export const BrandProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const querySlug = searchParams.get('company')?.trim().toLowerCase() || null;
  const [brand, setBrandState] = useState<CompanyBrand>(() => {
    const cached = readCachedBrand();
    return applyBrandTheme(cached || DEFAULT_COMPANY_BRAND);
  });
  const [companySlug, setCompanySlug] = useState<string | null>(() => {
    return querySlug || readCachedCompanySlug();
  });

  const setBrand = useCallback((nextBrand: CompanyBrand) => {
    const resolved = applyBrandTheme(normalizeCompanyBrand(nextBrand));
    setBrandState(resolved);
    cacheBrand(resolved);
  }, []);

  const loadBrandFromSlug = useCallback(async (slug: string) => {
    const resolved = normalizeCompanyBrand(await fetchBrandBySlug(slug));
    setBrand(resolved);
    cacheCompanySlug(slug);
    setCompanySlug(slug.trim().toLowerCase());
    return resolved;
  }, [setBrand]);

  useEffect(() => {
    if (!querySlug) return;
    cacheCompanySlug(querySlug);
    setCompanySlug(querySlug);
    void loadBrandFromSlug(querySlug);
  }, [loadBrandFromSlug, querySlug]);

  useEffect(() => {
    const token = localStorage.getItem('student_token');
    if (!token) return;

    void (async () => {
      const { fetchBrandForAuthenticatedUser } = await import('../services/brandApi');
      const profileBrand = await fetchBrandForAuthenticatedUser(token);
      if (profileBrand) {
        setBrand(profileBrand);
      }
    })();
  }, [setBrand]);

  const value = useMemo(
    () => ({ brand, companySlug, setBrand, loadBrandFromSlug }),
    [brand, companySlug, setBrand, loadBrandFromSlug]
  );

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
};

export const useBrand = (): BrandContextValue => useContext(BrandContext);
